/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LockIcon from '@mui/icons-material/Lock';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Tab, Tabs } from '@mui/material';

import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { useVertNavTabsStyles } from '../../styles/navigationTabs';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { ProfileTabs, tabToRouteBinding } from './AccountProfilePage.consts';
import { createInvitationsPageStyles } from './AccountProfilePage.styles';
import { ChangePassword } from './components/ChangePassword/ChangePassword';
import { DeleteAccount } from './components/DeleteAccount/DeleteAccount';
import { Notifications } from './components/Notifications/Notifications';
import { PersonalForm } from './components/PersonalForm/PersonalForm';

export const AccountProfilePage = memo(() => {
  const location = useLocation();
  const { t } = useTranslation();
  const tNs = Namespaces.UserProfile;
  const classes = createInvitationsPageStyles();
  const tabsClasses = useVertNavTabsStyles();
  useDocumentTitle(t(`${Namespaces.Common}:page_titles.profile`));
  const getPathForTab = (tab: ProfileTabs) => {
    return `${BasicRoutes.PROFILE}/${tabToRouteBinding[tab]}`;
  };

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.navigation}>
        <Tabs
          aria-label="profile user tabs"
          indicatorColor="primary"
          scrollButtons={false}
          value={location.pathname}
          classes={{ root: tabsClasses.root }}
          TabIndicatorProps={{ style: { display: 'none' } }}
          orientation="vertical"
        >
          <Tab
            value={tabToRouteBinding[ProfileTabs.Index]}
            label={t(`${tNs}:profile.personal_information`)}
            component={NavLink}
            to={tabToRouteBinding[ProfileTabs.Index]}
            icon={<AccountCircleIcon />}
            iconPosition="start"
          />
          <Tab
            value={getPathForTab(ProfileTabs.Security)}
            label={t(`${tNs}:profile.change_password`)}
            component={NavLink}
            to={tabToRouteBinding[ProfileTabs.Security]}
            icon={<LockIcon />}
            iconPosition="start"
          />
          <Tab
            value={getPathForTab(ProfileTabs.Notifications)}
            label={t(`${tNs}:profile.notifications`)}
            component={NavLink}
            to={tabToRouteBinding[ProfileTabs.Notifications]}
            icon={<NotificationsIcon />}
            iconPosition="start"
          />
          <Tab
            value={getPathForTab(ProfileTabs.DeleteAccount)}
            label={t(`${tNs}:profile.delete_account`)}
            component={NavLink}
            to={tabToRouteBinding[ProfileTabs.DeleteAccount]}
            icon={<DeleteForeverIcon />}
            iconPosition="start"
          />
        </Tabs>
      </div>
      <div className={classes.content}>
        <Routes>
          <Route index element={<PersonalForm />} />
          <Route path={tabToRouteBinding[ProfileTabs.Security]} element={<ChangePassword />} />
          <Route path={tabToRouteBinding[ProfileTabs.Notifications]} element={<Notifications />} />
          <Route path={tabToRouteBinding[ProfileTabs.DeleteAccount]} element={<DeleteAccount />} />
          <Route path="*" element={<Navigate to={tabToRouteBinding[ProfileTabs.Index]} />} />
        </Routes>
      </div>
    </div>
  );
});
